import { useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { TOAST_TYPE } from '@base'
import { deleteOrderDocumentApi, uploadBuyerInvoiceDocumentsApi, uploadExportDocumentsApi, uploadInvoiceDocumentApi } from '@data'
import { showLoader, showToast } from '@data/state/action'
import { updateDrawerMeta, toggleFileViewer } from '@data/state/action/root'
import { documentInvoiceType, strings } from '@constants'

// Array-valued invoice fields keep every uploaded file; the rest store a single path.
const ARRAY_FIELDS = ['tc_received', 'dkt_documents', 'product_photos']

// Coerce a field that may be an array, a single legacy string, or null into an array.
const toArray = (value) => (Array.isArray(value) ? value : value != null ? [value] : [])

/**
 * State + handlers for the Documents tab. Sources invoice/order from the drawer
 * state (same as useBasicDetails / useServicedQuantity) and ports the upload /
 * delete flows that previously lived inline in EditInvoice. Everything syncs back
 * through updateDrawerMeta — the tab does not depend on the parent `update()`.
 */
const useDocuments = (supplierPoNumber, update) => {
	const dispatch = useDispatch()
	const invoiceData = useSelector((state) => state.getIn(['root', 'drawerTypeMeta', 'invoiceData']))
	const order = useSelector((state) => state.getIn(['root', 'drawerTypeMeta', 'order']))
	const userData = useSelector((state) => state.getIn(['user', 'user']))

	const isBuyer = !supplierPoNumber
	const invoiceType = supplierPoNumber ? 'supplier' : 'buyer'

	const orgIds = useCallback(() => {
		if (supplierPoNumber) {
			const supplier = order?.supplier_list?.find((item) => item?.supplier_po_number === supplierPoNumber)?.supplier
			return { organisation_id: supplier?.organisation_id, userId: supplier?.user_id }
		}
		const buyer = order?.buyer_list?.[0]?.buyer
		return { organisation_id: buyer?.organisation_id, userId: buyer?.user_id }
	}, [order, supplierPoNumber])

	const uploadDoc = useCallback(
		(files, type, key) => {
			if (!files || files.length < 1) return
			dispatch(showLoader(true))
			const paths = Array.from(files)
			uploadBuyerInvoiceDocumentsApi(
				invoiceData.invoice_number,
				paths.length > 0 ? paths : [files],
				order.order_id,
				type === 'stockPhotos' ? null : documentInvoiceType[type],
				invoiceType
			)
				.then((response) => {
					const value = ARRAY_FIELDS.includes(key) ? [...toArray(invoiceData[key]), ...response.file_paths] : response.file_paths[0]
					dispatch(updateDrawerMeta({ invoiceData: { ...invoiceData, [key]: value } }))
					dispatch(showToast(true, strings('msg_document_uploaded'), { type: TOAST_TYPE.SUCCESS }))
					update?.()
				})
				.catch((err) => {
					dispatch(showToast(true, `${strings('document_upload_failed')} ${err}`, { type: TOAST_TYPE.ERROR }))
				})
				.finally(() => {
					dispatch(showLoader(false))
				})
		},
		[invoiceData, order, invoiceType, dispatch, update]
	)

	// Invoice document uses its own single-file endpoint keyed by PO number, not the
	// batch type-based upload the other cards share, so it gets a dedicated handler.
	const uploadInvoiceDoc = useCallback(
		(files) => {
			const file = Array.isArray(files) ? files[0] : files
			if (!file) return
			const poNumber = supplierPoNumber || order?.buyer_list?.[0]?.buyer_po_number
			dispatch(showLoader(true))
			uploadInvoiceDocumentApi(file, invoiceData?.invoice_number, order?.order_id, poNumber)
				.then(() => {
					dispatch(showToast(true, strings('msg_document_uploaded'), { type: TOAST_TYPE.SUCCESS }))
					update?.()
				})
				.catch((error) => {
					dispatch(showToast(true, error?.message ?? strings('error_something_went_wrong'), { type: TOAST_TYPE.ERROR }))
				})
				.finally(() => {
					dispatch(showLoader(false))
				})
		},
		[invoiceData, order, supplierPoNumber, dispatch, update]
	)

	const deleteDoc = useCallback(
		(filePath, type, key) => {
			if (!filePath) return
			const { organisation_id, userId } = orgIds()
			dispatch(showLoader(true))
			deleteOrderDocumentApi({
				filePath,
				orderId: order?.order_id,
				type: documentInvoiceType[type],
				invoiceNo: invoiceData?.invoice_number,
				organisation_id,
				userId,
			})
				.then((res) => {
					if (!res) {
						dispatch(showToast(true, strings('msg_document_delete_failed'), { type: TOAST_TYPE.ERROR }))
						return
					}
					const value = ARRAY_FIELDS.includes(key) ? toArray(invoiceData?.[key]).filter((item) => item !== filePath) : null
					dispatch(updateDrawerMeta({ invoiceData: { ...invoiceData, [key]: value } }))
					dispatch(showToast(true, strings('msg_document_deleted_success'), { type: TOAST_TYPE.SUCCESS }))
					update?.()
				})
				.catch((err) => {
					dispatch(showToast(true, err, { type: TOAST_TYPE.ERROR }))
				})
				.finally(() => {
					dispatch(toggleFileViewer(false))
					dispatch(showLoader(false))
				})
		},
		[invoiceData, order, orgIds, dispatch, update]
	)

	const uploadExportDoc = useCallback(
		(docType, files) => {
			if (!docType || !files || files.length < 1) return
			dispatch(showLoader(true))
			const paths = Array.from(files)
			uploadExportDocumentsApi(docType, paths.length > 0 ? paths : [files], invoiceData.invoice_number, order.order_id)
				.then((response) => {
					dispatch(updateDrawerMeta({ invoiceData: { ...response.data } }))
					dispatch(showToast(true, strings('msg_document_uploaded'), { type: TOAST_TYPE.SUCCESS }))
					update?.()
				})
				.catch((err) => {
					dispatch(showToast(true, `${strings('document_upload_failed')} ${err}`, { type: TOAST_TYPE.ERROR }))
				})
				.finally(() => {
					dispatch(showLoader(false))
				})
		},
		[invoiceData, order, dispatch, update]
	)

	const deleteExportDoc = useCallback(
		(filePath, type) => {
			if (!filePath) return
			const { organisation_id } = orgIds()
			dispatch(showLoader(true))
			deleteOrderDocumentApi({
				filePath,
				orderId: order?.order_id,
				type,
				invoiceNo: invoiceData?.invoice_number,
				userId: userData?.user_id,
				organisation_id,
			})
				.then((res) => {
					if (!res) {
						dispatch(showToast(true, strings('msg_document_delete_failed'), { type: TOAST_TYPE.ERROR }))
						return
					}
					const export_documents = invoiceData?.export_documents?.filter((item) => item.document_type !== type)
					dispatch(updateDrawerMeta({ invoiceData: { ...invoiceData, export_documents } }))
					dispatch(showToast(true, strings('msg_document_deleted_success'), { type: TOAST_TYPE.SUCCESS }))
					update?.()
				})
				.catch(() => {
					dispatch(showToast(true, strings('msg_document_delete_failed'), { type: TOAST_TYPE.ERROR }))
				})
				.finally(() => {
					dispatch(toggleFileViewer(false))
					dispatch(showLoader(false))
				})
		},
		[invoiceData, order, orgIds, userData, dispatch, update]
	)

	return { invoiceData, order, userData, isBuyer, uploadDoc, uploadInvoiceDoc, deleteDoc, uploadExportDoc, deleteExportDoc }
}

export default useDocuments
